import { useState, useEffect, useRef, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { AxiosError } from "axios";

import Menu from "./pages/Menu";
import IdleScreen from "./pages/IdleScreen";
import OrderType from "./pages/OrderType";
import OrderConfirmation from "./pages/OrderConfirmation";
import InvoiceRequest from "./pages/InvoiceRequest";
import Cart from "./pages/Cart";
import Payment from "./pages/Payment";
import Setup from "./pages/Setup";
import Settings from "./pages/Settings";
import ErrorScreen from "./pages/ErrorScreen";
import Maintenance from "./pages/Maintenance";
import { resolveConsumerRuntimeConnectionUuid } from "./runtime-endpoints";
import {
  applyPlatformMetadataToDocument,
  defaultPlatformMetadata,
  readPlatformMetadata,
} from "./platform-metadata";
import { applyTerminalTheme } from "./terminal-theme";
import { trn, useI18n } from "./shared/i18n";
import { readStoredCart, writeStoredCart } from "./cart-storage";
import { clearAllMenusStorage, readMenusUpdatedAt } from "./menu-cache";
import { connectConsumerRuntimeEvents } from "./runtime-events";
import { refreshBootstrap } from "./runtime-bootstrap";
import {
  installRuntimeAgentApi,
  type AgentActionDefinition,
  type AgentMacroContext,
  type AgentPermission,
  type AgentQueryActionDefinition,
} from "./agent/runtimeAgentApi";

const NATIVE_MENU_LONG_PRESS_MS = 5000;
const shouldUseFreshSetupBranding =
  import.meta.env.PROD && typeof window !== "undefined" && window.location.pathname === "/setup";

const hostname = window.location.hostname;
const storedPlatformAppName =
  typeof window !== "undefined" ? String(window.localStorage.getItem("platformAppName") || "").trim() : "";
const initialPlatformMetadata = shouldUseFreshSetupBranding
  ? defaultPlatformMetadata()
  : readPlatformMetadata();
let mode: "default" | "kiosk" | "table" | "qr" = "default";
let pageTitle = storedPlatformAppName || initialPlatformMetadata.appName || initialPlatformMetadata.platformName || "Kiosk";

if (hostname.includes("kiosk") || window.location.pathname === "/kiosk" || window.location.pathname.startsWith("/kiosk/")) {
  mode = "kiosk";
  pageTitle = storedPlatformAppName || initialPlatformMetadata.appName || initialPlatformMetadata.platformName || "Kiosk";
} else if (hostname.includes("table")) {
  mode = "table";
  pageTitle = storedPlatformAppName || initialPlatformMetadata.appName || initialPlatformMetadata.platformName || "Table";
} else if (hostname.includes("qr")) {
  mode = "qr";
  pageTitle = storedPlatformAppName || initialPlatformMetadata.appName || initialPlatformMetadata.platformName || "QR";
}
applyPlatformMetadataToDocument(initialPlatformMetadata, pageTitle);
applyTerminalTheme();
export { mode };

type NativeMenuBridge = {
  openNativeMenu?: () => unknown;
};

function readNativeMenuBridge(): NativeMenuBridge | null {
  const host = window as typeof window & {
    VolqanNativeAPI?: NativeMenuBridge;
    Android?: NativeMenuBridge;
  };
  const bridge =
    (host.VolqanNativeAPI && typeof host.VolqanNativeAPI === "object" ? host.VolqanNativeAPI : null) ||
    (host.Android && typeof host.Android === "object" ? host.Android : null);
  return bridge && typeof bridge.openNativeMenu === "function" ? bridge : null;
}

function openNativeMenuIfAvailable() {
  const bridge = readNativeMenuBridge();
  if (!bridge || typeof bridge.openNativeMenu !== "function") return;
  try {
    bridge.openNativeMenu();
  } catch {
    // Ignore native bridge errors on hidden gesture.
  }
}

const GlobalRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ready } = useI18n();
  const isConfigured = Boolean(resolveConsumerRuntimeConnectionUuid());
  const onSetup = location.pathname === "/setup";

  useEffect(() => {
    if (!isConfigured && !onSetup) navigate("/setup", { replace: true });
    if (import.meta.env.PROD && isConfigured && onSetup) navigate("/", { replace: true });
  }, [isConfigured, onSetup, navigate]);
  void ready;

  return null;
};

function App() {
  const { language } = useI18n();
  const nativeMenuLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialBootstrapStartedRef = useRef(false);
  const [cart, setCart] = useState(() => {
    return readStoredCart();
  });

  useEffect(() => {
    writeStoredCart(cart);
  }, [cart]);

  useEffect(() => {
    const hasBridge = Boolean(readNativeMenuBridge());
    if (!hasBridge) return;

    const clearLongPress = () => {
      if (!nativeMenuLongPressTimerRef.current) return;
      clearTimeout(nativeMenuLongPressTimerRef.current);
      nativeMenuLongPressTimerRef.current = null;
    };

    const startLongPress = () => {
      clearLongPress();
      nativeMenuLongPressTimerRef.current = setTimeout(() => {
        nativeMenuLongPressTimerRef.current = null;
        openNativeMenuIfAvailable();
      }, NATIVE_MENU_LONG_PRESS_MS);
    };

    window.addEventListener("touchstart", startLongPress, { capture: true, passive: true });
    window.addEventListener("touchend", clearLongPress, { capture: true, passive: true });
    window.addEventListener("touchcancel", clearLongPress, { capture: true, passive: true });
    window.addEventListener("mousedown", startLongPress, true);
    window.addEventListener("mouseup", clearLongPress, true);
    window.addEventListener("mouseleave", clearLongPress, true);
    window.addEventListener("blur", clearLongPress, true);

    return () => {
      clearLongPress();
      window.removeEventListener("touchstart", startLongPress, true);
      window.removeEventListener("touchend", clearLongPress, true);
      window.removeEventListener("touchcancel", clearLongPress, true);
      window.removeEventListener("mousedown", startLongPress, true);
      window.removeEventListener("mouseup", clearLongPress, true);
      window.removeEventListener("mouseleave", clearLongPress, true);
      window.removeEventListener("blur", clearLongPress, true);
    };
  }, []);

  useEffect(() => {
    return connectConsumerRuntimeEvents(() => {
      const connectionUuid = resolveConsumerRuntimeConnectionUuid();
      if (!connectionUuid) return;
      void refreshBootstrap(connectionUuid, { language: language || "es", refreshPlatformBranding: true, pageTitle }).catch(
        (error) => {
          const ax = error as AxiosError<any>;
          const status = ax.response?.status;
          if (status === 400 || status === 404) {
            localStorage.removeItem("connectionUuid");
            localStorage.removeItem("terminalId");
            localStorage.removeItem("sellingPointId");
            localStorage.removeItem("terminalConfig");
            localStorage.removeItem("restaurantName");
            localStorage.removeItem("restaurantLogo");
            localStorage.removeItem("marketplaceBackgroundImage");
            clearAllMenusStorage();
          }
        }
      );
    });
  }, [language]);

  useEffect(() => {
    const connectionUuid = resolveConsumerRuntimeConnectionUuid();
    if (!connectionUuid || initialBootstrapStartedRef.current) return;

    // main.tsx performs the blocking first bootstrap before mounting React.
    // Avoid immediately repeating that same request, while still revalidating
    // old browser state on subsequent starts.
    const initialMenuAge = Date.now() - readMenusUpdatedAt(language || "es");
    if (initialMenuAge >= 0 && initialMenuAge < 10_000) {
      initialBootstrapStartedRef.current = true;
      return;
    }

    initialBootstrapStartedRef.current = true;
    void refreshBootstrap(connectionUuid, {
      language: language || "es",
      refreshPlatformBranding: true,
      pageTitle,
    }).catch((error) => {
      initialBootstrapStartedRef.current = false;
      const ax = error as AxiosError<any>;
      const status = ax.response?.status;
      if (status === 400 || status === 404) {
        localStorage.removeItem("connectionUuid");
        localStorage.removeItem("terminalId");
        localStorage.removeItem("sellingPointId");
        localStorage.removeItem("terminalConfig");
        clearAllMenusStorage();
      }
    });
  }, [language]);

  useEffect(() => {
    const refreshSelectedLanguage = (event: Event) => {
      const requestedLanguage = String(
        (event as CustomEvent<{ language?: string }>).detail?.language || language || "es"
      ).trim();
      const connectionUuid = resolveConsumerRuntimeConnectionUuid();
      if (!connectionUuid || !requestedLanguage) return;
      void refreshBootstrap(connectionUuid, {
        language: requestedLanguage,
        refreshPlatformBranding: false,
        pageTitle,
      }).catch((error) => {
        console.warn("[runtime] selected language menu refresh failed:", error);
      });
    };

    window.addEventListener("menus:refresh-requested", refreshSelectedLanguage);
    return () => window.removeEventListener("menus:refresh-requested", refreshSelectedLanguage);
  }, [language]);

  return (
    <Router
      basename={window.location.pathname === "/kiosk" || window.location.pathname.startsWith("/kiosk/") ? "/kiosk" : "/"}
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <GlobalRedirect />
      <ConsumerRuntimeAgentBridge cartItemsCount={cart.length} />
      <Routes>
        <Route path="/" element={<IdleScreen />} />
        <Route path="/order-type" element={<OrderType />} />
        <Route path="/menu" element={<Menu cart={cart} setCart={setCart} />} />
        <Route path="/cart" element={<Cart cart={cart} setCart={setCart} />} />
        <Route path="/payment" element={<Payment cart={cart} setCart={setCart} />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />
        <Route path="/invoice-request" element={<InvoiceRequest />} />
        <Route path="/setup" element={<Setup />} />
        <Route path="/settings" element={<Settings setCart={setCart} />} />
        <Route path="/error" element={<ErrorScreen />} />
        <Route path="/maintenance" element={<Maintenance />} />
      </Routes>
    </Router>
  );
}

export default App;

function ConsumerRuntimeAgentBridge({ cartItemsCount }: { cartItemsCount: number }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, supportedLanguages } = useI18n();
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const runtimeAgentBridgeStateRef = useRef<{
    currentView: { id: string; label: string };
    language: string;
    actions: AgentActionDefinition[];
    permissions: AgentPermission[];
    queryActions: AgentQueryActionDefinition[];
    macroContext: AgentMacroContext;
    contextBlocks: Array<{
      id: string;
      title: string;
      data: unknown;
      updatedAt: string;
      optional?: boolean;
    }>;
    runtimeAgentLabels: Record<string, string>;
    cartItemsCount: number;
    locationPathname: string;
    locationSearch: string;
    connectionUuid: string;
    platformMetadata: ReturnType<typeof readPlatformMetadata>;
  } | null>(null);
  const connectionUuid = resolveConsumerRuntimeConnectionUuid();
  const platformMetadata = useMemo(() => readPlatformMetadata(), []);

  const currentView = useMemo(() => {
    switch (location.pathname) {
      case "/":
        return { id: "idle", label: "Idle screen" };
      case "/order-type":
        return { id: "order-type", label: "Order type" };
      case "/menu":
        return { id: "menu", label: "Menu" };
      case "/cart":
        return { id: "cart", label: "Cart" };
      case "/payment":
        return { id: "payment", label: "Payment" };
      case "/order-confirmation":
        return { id: "order-confirmation", label: "Order confirmation" };
      case "/invoice-request":
        return { id: "invoice-request", label: "Invoice request" };
      case "/setup":
        return { id: "setup", label: "Setup" };
      case "/settings":
        return { id: "settings", label: "Settings" };
      case "/maintenance":
        return { id: "maintenance", label: "Maintenance" };
      case "/error":
        return { id: "error", label: "Error" };
      default:
        return { id: "consumer-view", label: "Consumer runtime" };
    }
  }, [location.pathname]);

  const actions = useMemo<AgentActionDefinition[]>(
    () => [
      {
        id: "navigation.go_home",
        name: "Go Home",
        description: "Navigate to the idle screen.",
        parameters: [],
      },
      {
        id: "navigation.open_order_type",
        name: "Open Order Type",
        description: "Navigate to the order-type selector.",
        parameters: [],
      },
      {
        id: "navigation.open_menu",
        name: "Open Menu",
        description: "Navigate to the consumer menu.",
        parameters: [
          { name: "orderType", type: "string", required: false, description: "takeaway | eat-in" },
          { name: "tableCode", type: "string", required: false, description: "Optional table code for eat-in mode." },
        ],
      },
      {
        id: "navigation.open_cart",
        name: "Open Cart",
        description: "Navigate to the shopping cart.",
        parameters: [],
      },
      {
        id: "navigation.open_settings",
        name: "Open Settings",
        description: "Navigate to runtime settings.",
        parameters: [],
      },
      {
        id: "app.refresh",
        name: "Refresh Runtime",
        description: "Refresh consumer bootstrap data and current screen.",
        parameters: [],
      },
    ],
    []
  );

  const permissions = useMemo<AgentPermission[]>(
    () =>
      actions.map((action) => ({
        actionId: action.id,
        allowed: action.id !== "navigation.open_cart" || cartItemsCount > 0,
        reason: action.id === "navigation.open_cart" && cartItemsCount <= 0 ? "cart_empty" : undefined,
      })),
    [actions, cartItemsCount]
  );

  const queryActions = useMemo<AgentQueryActionDefinition[]>(
    () => [
      {
        id: "runtime.get_agent_labels",
        name: "Get Agent Labels",
        description: "Return localized labels and copy for the native container agent surface.",
        parameters: [],
      },
      {
        id: "runtime.get_navigation_state",
        name: "Get Navigation State",
        description: "Return current route, query string and current consumer view metadata.",
        parameters: [],
      },
      {
        id: "runtime.get_cart_summary",
        name: "Get Cart Summary",
        description: "Return current cart summary for the consumer runtime.",
        parameters: [],
      },
      {
        id: "runtime.get_terminal_context",
        name: "Get Terminal Context",
        description: "Return consumer terminal and publication context.",
        parameters: [],
      },
    ],
    []
  );

  const runtimeAgentLabels = useMemo(() => {
    const appLabel = currentView.label || platformMetadata.appName || platformMetadata.platformName || "Consumer Runtime";
    return {
      buttonShort: "AI",
      buttonAvailable: trn("app.consumer.agent.native.button_available", "AI · {app}", { app: appLabel }),
      buttonUnavailable: trn("app.consumer.agent.native.button_unavailable", "AI assistant unavailable"),
      dialogTitle: trn("app.consumer.agent.native.dialog_title", "AI Assistant"),
      dialogTitleWithApp: trn("app.consumer.agent.native.dialog_title_with_app", "AI Assistant · {app}", { app: appLabel }),
      dialogSubtitle: trn(
        "app.consumer.agent.native.dialog_subtitle",
        "Native container orchestration over Runtime Agent API and Cloud Agent."
      ),
      listening: trn("app.consumer.agent.native.listening", "Listening..."),
      processing: trn("app.consumer.agent.native.processing", "Processing..."),
      idle: trn("app.consumer.agent.native.idle", "Listen"),
      speakPrompt: trn("app.consumer.agent.native.speak_prompt", "Speak to the assistant"),
      voiceNotDetected: trn("app.consumer.agent.native.voice_not_detected", "No speech detected. Still listening."),
      unavailable: trn("app.consumer.agent.native.unavailable", "AI assistant unavailable"),
      confirmedPrefix: trn("app.consumer.agent.native.confirmed_prefix", "Confirmed:"),
      userPrefix: trn("app.consumer.agent.native.user_prefix", "You:"),
      assistantPrefix: trn("app.consumer.agent.native.assistant_prefix", "Assistant:"),
      systemPrefix: trn("app.consumer.agent.native.system_prefix", "System:"),
    };
  }, [currentView.label, language, platformMetadata.appName, platformMetadata.platformName]);

  const macroContext = useMemo<AgentMacroContext>(
    () => ({
      surface:
        mode === "kiosk"
          ? "kiosk"
          : mode === "table"
            ? "table_web"
            : mode === "qr"
              ? "qr_consumer_web"
              : "consumer_web",
      actorKind: "consumer",
      actorRole: mode === "kiosk" ? "kiosk_consumer" : "guest_consumer",
      currentAppId: currentView.id,
      currentAppLabel: currentView.label,
      authenticated: Boolean(connectionUuid),
      locationConfirmed: Boolean(connectionUuid),
      platform: typeof window !== "undefined" ? window.location.host : "web",
      runtimeFamily: "consumer",
      language: {
        active: language || "en",
        fallback: "en",
        supported: supportedLanguages?.length ? supportedLanguages : [language || "en"],
      },
      i18n: {
        runtimeProvidesAgentCopy: true,
        labelsQueryActionId: "runtime.get_agent_labels",
      },
    }),
    [currentView.id, currentView.label, language, supportedLanguages, connectionUuid]
  );

  const contextBlocks = useMemo(
    () => [
      {
        id: "consumer_runtime",
        title: "Consumer runtime state",
        updatedAt: new Date().toISOString(),
        data: {
          mode,
          path: location.pathname,
          search: location.search,
          language: language || "en",
          cartItemsCount,
        },
      },
      {
        id: "consumer_terminal",
        title: "Consumer terminal context",
        updatedAt: new Date().toISOString(),
        optional: true,
        data: {
          connectionUuid,
          appName: platformMetadata.appName || platformMetadata.platformName || "Consumer Runtime",
          platformName: platformMetadata.platformName || "",
          publicDomain: platformMetadata.publicDomain || "",
        },
      },
      {
        id: "agent_i18n",
        title: "Agent native labels",
        updatedAt: new Date().toISOString(),
        optional: true,
        data: runtimeAgentLabels,
      },
    ],
    [cartItemsCount, language, location.pathname, location.search, platformMetadata.appName, platformMetadata.platformName, platformMetadata.publicDomain, runtimeAgentLabels, connectionUuid]
  );

  runtimeAgentBridgeStateRef.current = {
    currentView,
    language: language || "en",
    actions,
    permissions,
    queryActions,
    macroContext,
    contextBlocks,
    runtimeAgentLabels,
    cartItemsCount,
    locationPathname: location.pathname,
    locationSearch: location.search,
    connectionUuid,
    platformMetadata,
  };

  useEffect(() => {
    const removeBridge = installRuntimeAgentApi({
      runtimeId: "volqan-consumer-app",
      runtimeLabel: "Volqan Consumer App",
      runtimeFamily: "consumer",
      runtimeKind: "consumer",
      appId: "idle",
      appLabel: "Consumer Runtime",
      language: "en",
      actions: [],
      permissions: [],
      queryActions: [],
      macroContext: {
        surface: "consumer_web",
        actorKind: "consumer",
        currentAppId: "idle",
        currentAppLabel: "Consumer Runtime",
        authenticated: false,
        locationConfirmed: false,
        platform: "web",
        runtimeFamily: "consumer",
      },
      getDefinition: () => {
        const state = runtimeAgentBridgeStateRef.current;
        return {
          version: "1" as const,
          runtime: {
            id: "volqan-consumer-app",
            label: "Volqan Consumer App",
            family: "consumer",
            kind: "consumer" as const,
          },
          agent: {
            available: (state?.actions.length || 0) > 0,
            language: state?.language || "en",
            assistantMode: "embedded_assistant" as const,
          },
          ui: {
            canOpenAssistant: true,
            canCloseAssistant: true,
          },
          capabilities: {
            supportsExecuteAction: true,
            supportsOpenAssistant: true,
            supportsCloseAssistant: true,
          },
          context: {
            currentAppId: state?.currentView.id || "idle",
            currentAppLabel: state?.currentView.label || "Consumer Runtime",
          },
          macroContext: state?.macroContext || {
            surface: "consumer_web",
            actorKind: "consumer",
            currentAppId: "idle",
            currentAppLabel: "Consumer Runtime",
            authenticated: false,
            locationConfirmed: false,
            platform: "web",
            runtimeFamily: "consumer",
          },
          contextBlocks: state?.contextBlocks || [],
          actions: state?.actions || [],
          queryActions: state?.queryActions || [],
          permissions: state?.permissions || [],
        };
      },
      assistantMode: "embedded_assistant",
      onOpenAssistant: () => setAgentPanelOpen(true),
      onCloseAssistant: () => setAgentPanelOpen(false),
      onExecuteAction: (actionId, parameters) => {
        const state = runtimeAgentBridgeStateRef.current;
        switch (actionId) {
          case "navigation.go_home":
            navigate("/", { replace: false });
            return "OK";
          case "navigation.open_order_type":
            navigate("/order-type", { replace: false });
            return "OK";
          case "navigation.open_menu": {
            const orderType = String(parameters.orderType || "takeaway").trim() || "takeaway";
            const tableCode = String(parameters.tableCode || "").trim();
            const query = orderType === "eat-in"
              ? `?type=eat-in${tableCode ? `&table=${encodeURIComponent(tableCode)}` : ""}`
              : "?type=takeaway";
            navigate(`/menu${query}`, { replace: false });
            return "OK";
          }
          case "navigation.open_cart":
            if ((state?.cartItemsCount || 0) <= 0) return "DENIED:CART_EMPTY";
            navigate("/cart", { replace: false });
            return "OK";
          case "navigation.open_settings":
            navigate("/settings", { replace: false });
            return "OK";
          case "app.refresh":
            if (!state?.connectionUuid) return "DENIED:NOT_CONFIGURED";
            void refreshBootstrap(state.connectionUuid, {
              language: state?.language || "es",
              refreshPlatformBranding: true,
              pageTitle,
            }).catch(() => undefined);
            return "OK";
          default:
            return "UNSUPPORTED_ACTION";
        }
      },
      onExecuteQuery: (queryActionId) => {
        const state = runtimeAgentBridgeStateRef.current;
        switch (queryActionId) {
          case "runtime.get_agent_labels":
            return JSON.stringify(state?.runtimeAgentLabels || {});
          case "runtime.get_navigation_state":
            return JSON.stringify({
              path: state?.locationPathname || "/",
              search: state?.locationSearch || "",
              currentView: state?.currentView || { id: "idle", label: "Idle screen" },
            });
          case "runtime.get_cart_summary":
            return JSON.stringify({
              cartItemsCount: state?.cartItemsCount || 0,
              canOpenCart: (state?.cartItemsCount || 0) > 0,
            });
          case "runtime.get_terminal_context":
            return JSON.stringify({
              connectionUuid: state?.connectionUuid || "",
              mode,
              platformMetadata: state?.platformMetadata || {},
            });
          default:
            return "UNSUPPORTED_QUERY";
        }
      },
    });
    return removeBridge;
  }, [navigate]);

  if (!agentPanelOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        right: "1rem",
        bottom: "1rem",
        width: "min(420px, calc(100vw - 2rem))",
        maxHeight: "min(70vh, 680px)",
        overflow: "auto",
        zIndex: 2147483645,
        borderRadius: "18px",
        border: "1px solid rgba(255,255,255,0.18)",
        background: "rgba(10, 19, 38, 0.96)",
        color: "#ffffff",
        boxShadow: "0 30px 80px rgba(0,0,0,0.42)",
        backdropFilter: "blur(14px)",
        padding: "1rem",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: "1.1rem", fontWeight: 800 }}>Consumer Runtime Agent</div>
          <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.72)" }}>
            {currentView.label} · {language || "en"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAgentPanelOpen(false)}
          style={{
            borderRadius: "999px",
            border: "1px solid rgba(255,255,255,0.24)",
            background: "rgba(255,255,255,0.08)",
            color: "#fff",
            padding: "0.45rem 0.9rem",
            fontWeight: 700,
          }}
        >
          Close
        </button>
      </div>

      <div style={{ marginTop: "0.9rem", fontSize: "0.9rem", color: "rgba(255,255,255,0.82)" }}>
        Runtime Agent API is active. The native container can inspect these actions and orchestrate them.
      </div>

      <div style={{ marginTop: "1rem", display: "grid", gap: "0.7rem" }}>
        {actions.map((action) => {
          const permission = permissions.find((entry) => entry.actionId === action.id);
          return (
            <div
              key={action.id}
              style={{
                borderRadius: "14px",
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
                padding: "0.85rem",
              }}
            >
              <div style={{ fontWeight: 800 }}>{action.name}</div>
              <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.74)", marginTop: "0.2rem" }}>
                {action.description}
              </div>
              <div style={{ fontSize: "0.8rem", marginTop: "0.55rem", color: permission?.allowed ? "#93c5fd" : "#fca5a5" }}>
                {permission?.allowed ? "Allowed" : `Blocked${permission?.reason ? ` · ${permission.reason}` : ""}`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
